import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import APIService from '../services/api';

const DashboardScreenSimple: React.FC = () => {
  const { user, logout } = useAuth();
  const navigation = useNavigation();
  const [walletBalance, setWalletBalance] = useState<string>('0.00');
  const [loadingWallet, setLoadingWallet] = useState<boolean>(true);
  
  console.log('📊 DashboardScreenSimple: Component rendered, user:', user?.name);

  // Función para cargar saldo de billetera virtual
  const loadWalletBalance = async () => {
    if (user) {
      try {
        setLoadingWallet(true);
        console.log('💰 DashboardScreenSimple: Cargando saldo de billetera virtual');
        const walletData = await APIService.getWalletBalance();
        setWalletBalance(walletData.saldo_billetera);
        console.log('💰 DashboardScreenSimple: Saldo cargado:', walletData.saldo_billetera);
      } catch (error) {
        console.error('❌ DashboardScreenSimple: Error al cargar billetera:', error);
        setWalletBalance('0.00');
      } finally {
        setLoadingWallet(false);
      }
    }
  };

  // Cargar saldo al montar el componente
  useEffect(() => {
    loadWalletBalance();
  }, [user]);

  // ✅ RECARGAR SALDO cada vez que la pantalla recibe el foco
  useFocusEffect(
    React.useCallback(() => {
      console.log('🔄 DashboardScreenSimple: Pantalla enfocada, recargando saldo');
      loadWalletBalance();
    }, [user])
  );

  const handleLogout = () => {
    console.log('🚪 DashboardScreenSimple: Logout button pressed');
    logout();
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.userInfo}>
            <Text style={styles.greeting}>¡Hola, {user?.name || 'Usuario'}! 👋</Text>
            <Text style={styles.subtitle}>Bienvenido a MotoMarketGT</Text>
            {user?.cliente_id && (
              <Text style={styles.clienteId}>Cliente ID: #{user.cliente_id}</Text>
            )}
          </View>
          
          <TouchableOpacity 
            onPress={handleLogout}
            style={styles.logoutButtonContainer}
            activeOpacity={0.7}
          >
            <Text style={styles.logoutButton}>Salir</Text>
          </TouchableOpacity>
        </View>

        {/* Información del Cliente */}
        {user && (
          <View style={styles.clienteCard}>
            <Text style={styles.clienteCardTitle}>Información del Cliente</Text>
            <View style={styles.clienteInfo}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>👤 Nombre:</Text>
                <Text style={styles.infoValue}>{user.name}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>🆔 ID Cliente:</Text>
                <Text style={styles.infoValue}>#{user.cliente_id || user.id}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>📧 Email:</Text>
                <Text style={styles.infoValue}>{user.email}</Text>
              </View>
              {user.telefono && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>📱 Teléfono:</Text>
                  <Text style={styles.infoValue}>{user.telefono}</Text>
                </View>
              )}
              {user.direccion && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>📍 Dirección:</Text>
                  <Text style={styles.infoValue}>{user.direccion}</Text>
                </View>
              )}
              {user.departamento && user.municipio && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>🌍 Ubicación:</Text>
                  <Text style={styles.infoValue}>{user.municipio}, {user.departamento}</Text>
                </View>
              )}
              {user.tipo_cliente && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>⭐ Tipo:</Text>
                  <Text style={[styles.infoValue, styles.premiumBadge]}>
                    {user.tipo_cliente.toUpperCase()}
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Billetera Virtual */}
        {user && (
          <View style={styles.walletCard}>
            <Text style={styles.walletCardTitle}>💰 Billetera Virtual</Text>
            <View style={styles.walletBalance}>
              <Text style={styles.walletLabel}>Saldo disponible:</Text>
              <View style={styles.walletAmountContainer}>
                {loadingWallet ? (
                  <Text style={styles.walletAmount}>Cargando...</Text>
                ) : (
                  <>
                    <Text style={styles.walletCurrency}>Q.</Text>
                    <Text style={styles.walletAmount}>{walletBalance}</Text>
                  </>
                )}
              </View>
            </View>
            <Text style={styles.walletDescription}>
              Acumula saldo en tu Billetera virtual con cada compra y úsalo en tus próximos pedidos
            </Text>
            
            {/* Botón Canjear Saldo */}
            <TouchableOpacity
              style={[
                styles.redeemButton,
                (parseFloat(walletBalance) <= 0 || loadingWallet) && styles.redeemButtonDisabled
              ]}
              onPress={() => {
                if (parseFloat(walletBalance) > 0 && !loadingWallet) {
                  console.log('💳 DashboardScreenSimple: Redirigiendo a búsqueda para canjear saldo');
                  (navigation as any).navigate('Search');
                } else {
                  alert('No tienes saldo disponible para canjear');
                }
              }}
              activeOpacity={parseFloat(walletBalance) > 0 && !loadingWallet ? 0.7 : 1}
            >
              <Text style={[
                styles.redeemButtonText,
                (parseFloat(walletBalance) <= 0 || loadingWallet) && styles.redeemButtonTextDisabled
              ]}>
                💳 🎁 Canjear Saldo
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Botones de navegación principales */}
        <View style={styles.section}>
          <View style={styles.buttonsContainer}>
            <TouchableOpacity 
              style={[styles.navButton, { backgroundColor: '#3498db' }]}
              onPress={() => (navigation as any).navigate('Search')}
            >
              <Text style={styles.navButtonText}>Ir a Búsqueda 🔍</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.navButton, { backgroundColor: '#e67e22' }]}
              onPress={() => (navigation as any).navigate('Cart')}
            >
              <Text style={styles.navButtonText}>Ver Carrito 🛒</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.navButton, { backgroundColor: '#9b59b6' }]}
              onPress={() => (navigation as any).navigate('Profile')}
            >
              <Text style={styles.navButtonText}>Mi Perfil 👤</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e8ed',
  },
  userInfo: {
    flex: 1,
  },
  greeting: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#7f8c8d',
    marginBottom: 4,
  },
  clienteId: {
    fontSize: 14,
    color: '#3498db',
    fontWeight: '600',
  },
  logoutButtonContainer: {
    padding: 12,
    backgroundColor: '#e74c3c',
    borderRadius: 8,
    minWidth: 60,
    minHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoutButton: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  section: {
    padding: 20,
  },
  buttonsContainer: {
    marginTop: 20,
  },
  navButton: {
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 3,
  },
  navButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  // Nuevos estilos para la información del cliente
  clienteCard: {
    margin: 20,
    marginTop: 0,
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  clienteCardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 15,
    textAlign: 'center',
  },
  clienteInfo: {
    gap: 10,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  infoLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    fontWeight: '600',
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    color: '#2c3e50',
    fontWeight: '500',
    flex: 2,
    textAlign: 'right',
  },
  premiumBadge: {
    color: '#f39c12',
    fontWeight: 'bold',
  },
  // Estilos para billetera virtual
  walletCard: {
    backgroundColor: 'white',
    margin: 20,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
  },
  walletCardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 15,
    textAlign: 'center',
  },
  walletBalance: {
    alignItems: 'center',
    marginBottom: 15,
  },
  walletLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 8,
  },
  walletAmountContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  walletCurrency: {
    fontSize: 20,
    color: '#27ae60',
    fontWeight: '600',
    marginRight: 5,
  },
  walletAmount: {
    fontSize: 32,
    color: '#27ae60',
    fontWeight: 'bold',
  },
  walletDescription: {
    fontSize: 12,
    color: '#7f8c8d',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 15,
  },
  redeemButton: {
    backgroundColor: '#27ae60',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 10,
  },
  redeemButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  redeemButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  redeemButtonTextDisabled: {
    color: '#7f8c8d',
  },
});

export default DashboardScreenSimple;
